import { FC, useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import Container from '../../../components/Container';
import { UserContext } from '../../..';
import { ROLE_ADMIN } from '../../../constants';
import { colors, fontWeights } from '../../../theme/tokens';
import PageViewer from '../../Archive/PageViewer';
import { DraftDocument } from '../../Archive/types';
import { HomeDocument } from '../../Home/types';
import { useDocumentActions } from '../../Home/useDocumentActions';
import { useDocumentResolvers } from '../../Home/useDocumentResolvers';
import EditDocumentDialog from '../Edit';
import { SESSION_STORAGE_DOCUMENT_DELETED_KEY } from '../constants';
import { useDocumentView } from '../useDocumentView';
import DocumentSheet from './DocumentSheet';
import VerificationHistory from './VerificationHistory';

// The record of a document, `/document/:id` (no design in the XD, gap 3).
//
// Full screen without tiles, like Résultats (screen 7): "‹ Retour" that
// returns to the list it was reached from (`location.state.from`, or "Mes
// documents"), the title with the "Confidentiel" chip, and two columns on
// desktop: the wizard's page navigator (PageViewer, with zoom,
// thumbnails, "‹ Page N / M ›" and full screen) on the left, the read-only
// record (DocumentSheet) on the right. A document archived through
// the previous path, without pages, shows its single file as a page
// (PDF in <iframe>).
//
// Buttons: "Télécharger" (orange, for whoever can read), "Modifier" and
// "Supprimer" only for the administrator or the archiver — the same as
// `documentAccess.canWrite` in the backend, which would answer 403 anyway.
// Deleting asks for confirmation (useDocumentActions, the same dialog as
// the Home) and returns to the list, which shows the notice.
//
// `/document/edit/:id` is this same screen with the "Modifier le
// document" modal on top (`editing`), just as `/view-user/:id` over the users
// list; both routes mount this component so that the document
// is not requested again (nor counted as a visit) when the modal opens.
//
// The visit is recorded by the backend in `GET /archives/document` (see
// useDocumentView). A 404 (nonexistent, or someone else's confidential: the backend
// does not distinguish on purpose) leads to /404, like the user record.
//
// Mobile (ROADMAP.md, Part 2): single column; the viewer is a thumbnail that
// opens the full screen; the buttons, stacked at the end at full width.

type Props = {
  editing?: boolean;
};

const DEFAULT_BACK = '/my-documents';

// What useDocumentActions expects: the listing row. The record's document
// brings the same with the dates possibly null (draft).
const asRow = (document: DraftDocument): HomeDocument => ({
  id: document.id,
  title: document.title,
  issuing_date: document.issuing_date ?? '',
  archiving_date: document.archiving_date ?? '',
  service: document.service,
  issuer: document.issuer,
  archiver: document.archiver,
  confidential: document.confidential,
  verification_url: document.verification_url,
});

const ViewDocument: FC<Props> = ({ editing = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const from: string =
    (location.state as { from?: string } | null)?.from ?? DEFAULT_BACK;

  const { state, document, image, pages, viewerPages, isLegacy, refresh, retry } =
    useDocumentView(id);
  const { userName, serviceName, resolvers } = useDocumentResolvers();
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    window.document.title = editing
      ? 'ArchivOs - Modifier un document'
      : 'ArchivOs - Voir un document';
  }, [editing]);

  useEffect(() => {
    if (state === 'missing') navigate('/404', { replace: true });
  }, [state, navigate]);

  // The displayed page cannot point beyond the last one.
  useEffect(() => {
    if (pageIndex > 0 && pageIndex >= viewerPages.length) {
      setPageIndex(Math.max(viewerPages.length - 1, 0));
    }
  }, [viewerPages.length, pageIndex]);

  const leave = () => navigate(from);

  const { onAction, notify, elements: actionElements } = useDocumentActions({
    onDeleted: () => {
      sessionStorage.setItem(SESSION_STORAGE_DOCUMENT_DELETED_KEY, 'true');
      leave();
    },
    onVanished: leave,
  });

  const row = document ? asRow(document) : null;
  const canWrite = row ? resolvers.canWrite(row) : false;
  const isAdmin = useContext(UserContext)?.mainUser?.role === ROLE_ADMIN;

  // Without write permission there is no edit modal: the route returns to the
  // record instead of showing what the backend would reject.
  useEffect(() => {
    if (editing && state === 'ready' && !canWrite && id) {
      navigate(`/document/${id}`, { replace: true, state: { from } });
    }
  }, [editing, state, canWrite, id, navigate, from]);

  const closeEdit = () => {
    if (id) navigate(`/document/${id}`, { state: { from } });
  };

  const actions = row && (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        flexWrap: 'wrap',
        flexDirection: isMobile ? 'column' : 'row',
        '& > .MuiButton-root': isMobile ? { width: '100%' } : undefined,
      }}
    >
      <Button
        variant="contained"
        color="secondary"
        startIcon={<FileDownloadOutlinedIcon />}
        onClick={() => onAction('download', row)}
      >
        Télécharger
      </Button>
      {canWrite && (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<EditOutlinedIcon />}
          onClick={() => navigate(`/document/edit/${row.id}`, { state: { from } })}
        >
          Modifier
        </Button>
      )}
      {canWrite && (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<DeleteOutlinedIcon />}
          onClick={() => onAction('delete', row)}
        >
          Supprimer
        </Button>
      )}
    </Box>
  );

  return (
    <Container showTiles={false}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Link
          component={RouterLink}
          to={from}
          variant="actionLink"
          underline="hover"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            color: colors.accentLight,
          }}
        >
          <ChevronLeftIcon fontSize="small" />
          Retour
        </Link>

        {(state === 'loading' || state === 'missing') && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {state === 'error' && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography role="alert" sx={{ mb: 2 }}>
              Impossible de charger le document.
            </Typography>
            <Button variant="outlined" color="primary" onClick={retry}>
              Réessayer
            </Button>
          </Box>
        )}

        {state === 'ready' && document && row && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  component="h2"
                  variant="h2"
                  data-testid="document-title"
                  sx={{ color: colors.primary, overflowWrap: 'anywhere' }}
                >
                  {document.title?.trim() || 'Document sans titre'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {document.confidential && (
                    <Chip
                      icon={<LockOutlinedIcon />}
                      label="Confidentiel"
                      size="small"
                      variant="outlined"
                      sx={{
                        color: colors.accent,
                        borderColor: colors.accent,
                        fontWeight: fontWeights.semiBold,
                        '& .MuiChip-icon': { color: colors.accent },
                      }}
                    />
                  )}
                  {document.status === 'draft' && (
                    <Chip
                      label="Brouillon"
                      size="small"
                      variant="outlined"
                      sx={{ color: colors.muted, borderColor: colors.border }}
                    />
                  )}
                </Box>
              </Box>
              {!isMobile && actions}
            </Box>

            <Box
              sx={(t) => ({
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)',
                columnGap: 4,
                rowGap: 3,
                alignItems: 'start',
                [t.breakpoints.down('tablet')]: {
                  gridTemplateColumns: 'minmax(0, 1fr)',
                },
              })}
            >
              <Box sx={{ color: colors.textBase }}>
                <PageViewer
                  pages={viewerPages}
                  index={pageIndex}
                  onIndexChange={setPageIndex}
                  tone="light"
                  canvasHeight="clamp(320px, 60vh, 720px)"
                />
              </Box>

              <DocumentSheet
                document={document}
                serviceName={serviceName(document.service)}
                issuerName={userName(document.issuer)}
                archiverName={userName(document.archiver)}
                pageCount={pages.length}
                isLegacy={isLegacy}
              />
            </Box>

            {/* The public verification journal is for administrators only. */}
            {isAdmin && document.status === 'archived' && (
              <VerificationHistory documentId={document.id} />
            )}

            {isMobile && actions}
          </>
        )}
      </Box>

      {actionElements}

      {state === 'ready' && document && canWrite && (
        <EditDocumentDialog
          open={editing}
          document={document}
          image={image}
          pages={pages}
          onClose={closeEdit}
          onSaved={(notice) => {
            refresh();
            notify(notice);
            closeEdit();
          }}
          onFileChanged={refresh}
        />
      )}
    </Container>
  );
};

export default ViewDocument;
